import moment, { isMoment } from 'moment-timezone'
import { timeDay, timeParse } from 'd3'
import { getLastElemOfArr } from 'containers/helpers'

export const shortDateFormat = (ts, locale) =>
	moment(ts).format(locale === 'en' ? 'MM/DD' : 'DD/MM')

const parseTimestamp = timeParse("%s")

export const dateByDateFormat = (date, format, locale) => moment(date, format).locale(locale).format('dddd Do MMMM YYYY')
export const momentFromDateString = (dateString) => moment(dateString, 'DD/MM/YYYY')


export const floorTimestampFromAPIToUnix = (date) =>
	Math.floor(timeDay.floor(moment.unix(timestampFromAPIToUnix(date)).toDate()).getTime() / 1000)

export const floorDate = (dateString) => {
	return timeDay.floor(parseTimestamp(dateString)).getTime() / 1000
}

export const timeDateFormat = (date) => moment(date).format('YYYY-MM-DD HH:mm')
export const getDateFormatted = (date) => moment(date).format('YYYY-MM-DD')

export const timestamp2date = (ts) => new Date(Date.parse(ts))

export const stripFullTimezoneName = (dateStr) => dateStr.split('(')[0].trim()

export const timestampFromAPIToUnix = (date) => timestamp2unix(new Date(date))

export const dateObj2dateKey = (date) => date2unix(date)

export const timestamp2unix = (ts) => date2unix(Date.parse(ts))

export const date2unix = (date) => Math.floor(date.valueOf() / 1000)

export const moment2unix = (date) => date2unix(date.toDate())

export const addDays = (date, days) => {
	const result = new Date(date)
	result.setDate(result.getDate() + days)
	return result
}

export const timestamp2mili = (ts) => Math.floor(ts * 1000)

export const hasWeekends = ({ start, end }) => {
	if (isWeekend(start) || isWeekend(end)) {
		return true
	}
	return Math.abs(moment.unix(start).diff(moment.unix(end), 'days')) > 6
}

export const convertTimeZoneFromDate = (date) => {
	const parisTimezone = 'Europe/Paris'
	const m = moment.unix(date).tz(parisTimezone)
	return m.format("YYYY-MM-DDTHH:mm:ss.SSS")
}

export const onlyWeekends = (dates) => {
	for (const date of dates) {
		if (!isWeekend(date)) {
			return false
		}
	}
	return true
}

export const getWeekendDaysInRange = ({ start, end }) => {
	let dates = []
	let current = start

	while (current < end) {
		if (isWeekend(current)) {
			dates.push(current)
		}
		current = addDays(current, 1)
	}
	return dates
}

export const removeWeekends = (dates) =>
	dates.filter((date) => !isWeekend(date))

export const isWeekend = (date) => {
	const _date = moment.unix(date)
	const dayOfWeek = _date.day()
	return dayOfWeek === 0 || dayOfWeek === 6
}

export const dayDateFormatFromAPI = (date) =>
	moment.unix(timestampFromAPIToUnix(date)).format('dddd, MMMM Do YY')
export const dayDateFormat = (date, locale) =>
	moment.unix(date).locale(locale).format('dddd Do MMMM YYYY')
export const getDatesFromRecurrence = ({ start, end, onDays, repeats }) => {
	let dates = []
	let currentDate = moment(start)

	for (let i = 0; currentDate <= end; i++) {
		const currentWeekDay = moment(currentDate).isoWeekday()
		for (let j = 0; j < onDays?.length; j++) {
			if (currentWeekDay <= Number(onDays[j])) {
				dates.push(moment(currentDate).isoWeekday(onDays[j]))
			} else {
				dates.push(
					moment(currentDate).add(1, 'weeks').isoWeekday(onDays[j])
				)
			}
		}
		currentDate = moment(currentDate).add(repeats, 'weeks')
	}
	return dates
}

export const toDateInput = (date) => moment(new Date(date)).format('YYYY-MM-DD')

export const inDateRange = (date, startDate, endDate) =>
	startDate <= date && date <= endDate

export const isEqual = (d1, d2) => {
	return +d1 == +d2
}

export const utc2gmt = (dateFromPostgres) => {
	const unix = timestampFromAPIToUnix(dateFromPostgres)
	const converted = convertTimeZoneFromDate(unix)
	return converted
}

export const getEndWorkHourToday = () => {
	const today = new Date()
	today.setHours(23, 59, 59)
	return today
}

export const padTo2Digits = (num) => {
	return num.toString().padStart(2, '0');
}

export const formatDate = (date) =>
	isMoment(date) ? date.format('DD/MM/YYYY') : moment(date).format('DD/MM/YYYY')

export const formatDateWithHour = (date) => {
	return [
		padTo2Digits(date.getDate()),
		padTo2Digits(date.getMonth() + 1),
		date.getFullYear(),
	].join('/') + ' ' + [
		padTo2Digits(date.getHours()),
		padTo2Digits(date.getMinutes()),
	].join(':');
}


export const sortDateArr = (dateArr = [new Date(), new Date()]) =>
	dateArr.sort((a, b) => a - b)

export const getStartAndEndDate = (dateArr) => {
	const sortedArr = sortDateArr(dateArr)
	return {
		startDate: sortedArr[0],
		endDate: getLastElemOfArr(sortedArr),
	}
}

export const getTodayMidnight = () => {
	const d = new Date();
	d.setHours(0, 0, 0, 0);
	return d
}

export const getFirstDayOfMonth = date => new Date(date.getFullYear(), date.getMonth(), 1)

export const getLastDayOfMonth = date => new Date(date.getFullYear(), date.getMonth() + 1, 0)

export const getExtremesDatesOfMonth = (dateFromParams = null) => {
	const date = dateFromParams || new Date()
	const firstDay = getFirstDayOfMonth(date);
	const lastDay = getLastDayOfMonth(date)
	return { startDate: firstDay, endDate: lastDay }
}



export const compareDiffTZDates = (d1, d2) =>
	formatDate(new Date(convertTimeZoneFromDate(d1)))
	===
	formatDate(new Date(convertTimeZoneFromDate(d2)))

export const getMostRecentDate = (dates = []) => dates
	.map(d => isMoment(d) ? d : moment(d))
	.sort((a, b) => b.unix() - a.unix())[0]

export const getOlderDate = (dates = []) => dates
	.map(d => isMoment(d) ? d : moment(d))
	.sort((a, b) => a.unix() - b.unix())[0]

export const getExtremesOfYear = (momentDate) => {
	var startDate = momentDate.clone().startOf('year');
	var endDate = momentDate.clone().endOf('year');
	return [startDate, endDate];
}
export const getExtremesOfMonth = (momentDate) => {
	var startDate = momentDate.clone().startOf('month');
	var endDate = momentDate.clone().endOf('month');
	return [startDate, endDate];
}

export const getHoursCountFromSlots = slots => slots.length / 4
